
  import { computed, defineComponent, onMounted, ref } from 'vue';
  import { ErrorMessage, Field, Form } from 'vee-validate';
  import { setCurrentPageBreadcrumbs } from '@/core/helpers/breadcrumb';
  import { useI18n } from 'vue-i18n/index';
  import { useStore } from 'vuex';
  import { useRouter, useRoute } from 'vue-router';
  import { Actions } from '@/store/enums/StoreEnums';
  import { LocationObject } from '@/store/modules/Auctions/LocationsModule';
  import { useAbility } from '@casl/vue';
  import Swal from 'sweetalert2/dist/sweetalert2.js';
  import * as Yup from 'yup';

  export default defineComponent({
    name: 'locations-editing',
    components: {
      ErrorMessage,
      Field,
      Form,
    },
    async setup() {
      const { t, te } = useI18n();
      const store = useStore();
      const router = useRouter();
      const route = useRoute();
      const { can } = useAbility();
      const submitButton = ref<HTMLButtonElement | null>(null);

      onMounted(() => {
        setCurrentPageBreadcrumbs(translate('locationUpdating'), [
          translate('auctions'),
        ]);
      });

      const { data } = await store.dispatch(
        Actions.GET_LOCATION,
        route.params.id
      );

      const location = ref<LocationObject>(data.data);

      const translate = (text: string) => {
        if (te(text)) {
          return t(text);
        } else {
          return text;
        }
      };

      const goBack = () => {
        router.back();
      };

      const validationSchema = Yup.object().shape({
        organiserId: Yup.number()
          .required(() => translate('ORGANIZER_IS_REQUIRED_FIELD'))
          .label('Organizer'),
        venueEn: Yup.string()
          .required(() => translate('EN_VENUE_IS_REQUIRED_FIELD'))
          .label('Venue English'),
        venueAr: Yup.string()
          .required(() => translate('AR_VENUE_IS_REQUIRED_FIELD'))
          .label('Venue Arabic'),
        longitude: Yup.number()
          .required(() => translate('LATITUDE_IS_REQUIRED_FIELD'))
          .test(
            'longitude',
            translate('INVALID_LONGITUDE'),
            (num) => !num || (isFinite(num) && Math.abs(num) <= 90)
          )
          .label('Longitude'),
        latitude: Yup.number()
          .required(() => translate('LONGITUDE_IS_REQUIRED_FIELD'))
          .test(
            'latitude',
            translate('INVALID_LATITUDE'),
            (num) => !num || (isFinite(num) && Math.abs(num) <= 180)
          )
          .label('Latitude'),
      });

      const onSubmitUpdate = async (values) => {
        if (submitButton.value) {
          // eslint-disable-next-line
          submitButton.value!.disabled = true;
          submitButton.value.setAttribute('data-kt-indicator', 'on');
        }

        await store.dispatch(Actions.UPDATE_LOCATION, {
          id: location.value?.id,
          data: values,
        });
        const [errorName] = Object.keys(store.getters.getErrors);
        const error = store.getters.getErrors[errorName];

        if (error) {
          Swal.fire({
            text: translate(error[0]),
            icon: 'error',
            buttonsStyling: false,
            confirmButtonText: translate('tryAgainExcl'),
            customClass: {
              confirmButton: 'btn fw-bold btn-light-danger',
            },
          });
          //Deactivate indicator
          submitButton.value?.removeAttribute('data-kt-indicator');
          // eslint-disable-next-line
          submitButton.value!.disabled = false;
        } else {
          Swal.fire({
            text: translate('SUCCESSFULLY_UPDATED_LOCATION'),
            icon: 'success',
            buttonsStyling: false,
            confirmButtonText: translate('ok'),
            customClass: {
              confirmButton: 'btn btn-light-primary',
            },
          }).then(() => {
            router.push({ name: 'locations-listing' });
          });
        }
      };

      await store.dispatch(Actions.GET_ORGANIZERS, { limit: 50 });

      store.dispatch(Actions.REMOVE_BODY_CLASSNAME, 'page-loading-over');

      return {
        onSubmitUpdate,
        submitButton,
        translate,
        validationSchema,
        goBack,
        location,
        can,
        organizers: computed(() => store.getters.organizersList),
      };
    },
  });
